// Festival greeting calendar for the ITO marketing hero.
// On a festival day (India / IST), the homepage hero is replaced by a festive
// greeting hero (AI-generated Indian-logistics artwork + logo + a logistics quote).
//
// - `annual`  : fixed Gregorian date, recurs every year { month, day }.
// - `annualRange` : fixed multi-day Gregorian window { month, from, to }.
// - `dates`   : explicit ISO dates for lunar/variable festivals (per year).
// Add new years' lunar dates to the `dates` arrays as they are announced.

const CDN =
  "https://static.prod-images.emergentagent.com/jobs/8482b3ea-b271-4a0f-b733-76a563b58ed7/images/";

export const FESTIVALS = [
  {
    key: "new_year",
    eyebrow: "Season's greetings from ITO",
    name: "Happy New Year",
    accent: "#F5B301",
    image: CDN + "9d17f8326593da739f42a2ff1e522e8a49a273a6ca6b1180bc52bf139388916e.jpeg",
    quote:
      "A new year, fresh routes, the same promise — your cargo delivered safely to every pincode in India. Here's to another year of moving India forward, together.",
    annual: { month: 1, day: 1 },
  },
  {
    key: "makar_sankranti",
    eyebrow: "Season's greetings from ITO",
    name: "Happy Makar Sankranti",
    accent: "#F2A03D",
    image: CDN + "7d3a8e441d9c72cdb06be0354bd771c3481f03005fe79d68542ff318b76bc67e.jpeg",
    quote:
      "As kites soar and the harvest comes home, so does every consignment we carry. May this Sankranti bring prosperity to your business and smooth roads to ours.",
    annualRange: { month: 1, from: 13, to: 15 },
    regional: {
      south: {
        name: "Happy Pongal",
        quote:
          "As the first rice of the harvest boils over in every home, may prosperity overflow in your business too. From our family to yours across the South — a very happy Pongal.",
      },
      northeast: {
        name: "Happy Magh Bihu",
        quote:
          "Bonfires, feasts and a bountiful harvest — Magh Bihu is a season of plenty. May your granaries and your order books stay full all year. Happy Bihu!",
      },
      north: {
        name: "Happy Lohri & Makar Sankranti",
        quote:
          "Around the Lohri fire we celebrate the harvest and the promise of warmer, brighter, busier days ahead. Wishing you a joyous Lohri and a prosperous Sankranti.",
      },
      west: {
        name: "Happy Uttarayan",
        quote:
          "As the skies of Gujarat fill with kites, may your business soar just as high this Uttarayan. Wishing you clear skies and smooth roads all year.",
      },
    },
  },
  {
    key: "republic_day",
    eyebrow: "Proud to serve the nation",
    name: "Happy Republic Day",
    accent: "#138808",
    image: CDN + "1a76eb386a452484b09ab4ee50593421a1c16c9948acbb9673fd79b1187745db.jpeg",
    quote:
      "From border convoys to the heart of India's industry, we carry the nation's most critical cargo with pride. Saluting the Republic that keeps our wheels turning since 1972.",
    annual: { month: 1, day: 26 },
  },
  {
    key: "holi",
    eyebrow: "Season's greetings from ITO",
    name: "Happy Holi",
    accent: "#E23E8B",
    image: CDN + "f1094d749d662c44907eebd62fc605e205186ce11c410a9617a2864fb381ca28.jpeg",
    quote:
      "A festival of colour deserves a fleet that never misses a beat. This Holi, may your consignments and your celebrations both arrive full of joy.",
    dates: ["2026-03-04", "2027-03-22"],
  },
  {
    key: "eid_fitr",
    eyebrow: "Season's greetings from ITO",
    name: "Eid Mubarak",
    accent: "#E0A93B",
    image: CDN + "791ee2cfd81fbe11915a147798315099c0b220f2892098865c123d750ee50e24.jpeg",
    quote:
      "As the crescent rises, we keep the highways moving so your cargo reaches home in time to celebrate. Wishing you a blessed and peaceful Eid across every route.",
    dates: ["2026-03-20", "2026-03-21", "2027-03-10"],
  },
  {
    key: "eid_adha",
    eyebrow: "Season's greetings from ITO",
    name: "Eid al-Adha Mubarak",
    accent: "#D99A32",
    image: CDN + "51d52a68aad38befac60c22fb01540f71fc43511fc2f0bda88e1799f1208c19d.jpeg",
    quote:
      "A festival of sacrifice and giving — values we carry with every load, to every corner of India. Wishing you and your family a blessed Bakrid.",
    dates: ["2026-05-27", "2027-05-17"],
  },
  {
    key: "independence_day",
    eyebrow: "Proud to serve the nation",
    name: "Happy Independence Day",
    accent: "#FF9933",
    image: CDN + "0ed48720178100091c08f4c942c723f54d44579d4b731a113e5c8a21c8b92683.jpeg",
    quote:
      "Every mile we drive keeps a free, self-reliant India moving. From defence stores to industry, we've delivered the nation's cargo for over five decades. Jai Hind.",
    annual: { month: 8, day: 15 },
  },
  {
    key: "onam",
    eyebrow: "Season's greetings from ITO",
    name: "Happy Onam",
    accent: "#F0A500",
    image: CDN + "637c4cedfbc4343ac563723108ca304c0d35666bbfa920c5e37d1ba858c33aff.jpeg",
    quote:
      "From Kerala's backwaters to the borders of Ladakh, our network reaches every home. May this Onam bring abundance to your table and reliability to your supply chain.",
    dates: ["2026-08-26", "2027-09-12"],
  },
  {
    key: "raksha_bandhan",
    eyebrow: "Season's greetings from ITO",
    name: "Happy Raksha Bandhan",
    accent: "#E5484D",
    image: CDN + "1d67b16d87e9633b79081cb3234ff7095bd2226b92806e1c7ede84a29e3beaa5.jpeg",
    quote:
      "A bond of trust and protection — the very promise we make to every consignment we carry. This Raksha Bandhan, we vow to keep your cargo safe, mile after mile.",
    dates: ["2026-08-28", "2027-08-17"],
  },
  {
    key: "ganesh_chaturthi",
    eyebrow: "Season's greetings from ITO",
    name: "Happy Ganesh Chaturthi",
    accent: "#E07B39",
    image: CDN + "602a4f69d6b89e5e31ad149f1caff6556abda3a5a7712bf7c21c347562656c25.jpeg",
    quote:
      "Ganpati Bappa Morya! May the remover of obstacles clear every road ahead — for your business and for the cargo we carry across India.",
    dates: ["2026-09-14", "2027-09-04"],
  },
  {
    key: "dussehra",
    eyebrow: "Season's greetings from ITO",
    name: "Happy Dussehra",
    accent: "#D9483B",
    image: CDN + "58ab8fd8b42a70d9978f55f82ae02f66785db4c2fac4df1988a244389ee3a01e.jpeg",
    quote:
      "A day when good triumphs over every obstacle — just as our teams conquer the toughest routes to keep your cargo moving. Wishing you a victorious Vijayadashami.",
    dates: ["2026-10-20", "2027-10-09"],
  },
  {
    key: "gandhi_jayanti",
    eyebrow: "Honouring the Mahatma",
    name: "Gandhi Jayanti",
    accent: "#4C9A2A",
    image: CDN + "41612c166a6fb0d64c6756742dd30e02c48d3530412e55935e2952f127741a80.jpeg",
    quote:
      "Simplicity, honesty and service to the nation — the principles that steer every journey we make. Remembering the Father of the Nation on his birth anniversary.",
    annual: { month: 10, day: 2 },
  },
  {
    key: "diwali",
    eyebrow: "Season's greetings from ITO",
    name: "Happy Diwali",
    accent: "#F5A623",
    image: CDN + "9e63b9d4d8ea100d8dbc699184dec7377cd8e0d946b0f9a35ba4e18937c3b3e7.jpeg",
    quote:
      "This Diwali, we light up every route across India so your cargo reaches home before the celebrations begin. Wishing you a festival of prosperity, safety and light.",
    dates: ["2026-11-08", "2027-10-29"],
  },
  {
    key: "guru_nanak",
    eyebrow: "Season's greetings from ITO",
    name: "Happy Gurpurab",
    accent: "#E0A93B",
    image: CDN + "43a1655316d3c86bcbaec6a0dbe561935c4a4badedc72f8c3f114d28767727b6.jpeg",
    quote:
      "Guru Nanak's message of service and honesty guides every mile we travel. On this Gurpurab, may his light bring peace and prosperity to your journey.",
    dates: ["2026-11-24", "2027-11-14"],
  },
  {
    key: "christmas",
    eyebrow: "Season's greetings from ITO",
    name: "Merry Christmas",
    accent: "#C0392B",
    image: CDN + "9d8aff15ef8ee876e24892dd92d7c0683ab6e7c5853482c9c5b67c5bfe00894a.jpeg",
    quote:
      "From the snow-bound roads of Ladakh to the plains of India, we're delivering right up to Christmas eve. Wishing you warmth, joy and cargo that always arrives on time.",
    annual: { month: 12, day: 25 },
  },
  {
    key: "maha_shivratri",
    eyebrow: "Season's greetings from ITO",
    name: "Happy Maha Shivratri",
    accent: "#6C63C4",
    image:
      "https://static.prod-images.emergentagent.com/jobs/9cbd5b9e-f8ce-4d41-86d0-1a1eddd399f3/images/e939b14abdb00a12541edededde9e7a7ca3462c66505094f1d0e988d17aa3f6b.jpeg",
    quote:
      "On the night of Mahadev, we keep the toughest mountain routes moving so nothing stops for your business. Har Har Mahadev — wishing you strength, focus and peace this Maha Shivratri.",
    dates: ["2026-02-15", "2027-03-06"],
  },
  {
    key: "ram_navami",
    eyebrow: "Season's greetings from ITO",
    name: "Happy Ram Navami",
    accent: "#E4772E",
    image:
      "https://static.prod-images.emergentagent.com/jobs/9cbd5b9e-f8ce-4d41-86d0-1a1eddd399f3/images/310d810c950ea120dd68ecd0374b375fcba726d0e5df93b3483b35fad545a920.jpeg",
    quote:
      "Lord Ram's journey teaches us that no distance is too great when the purpose is true. This Ram Navami, may your path be righteous and every consignment reach its destination.",
    dates: ["2026-03-26", "2027-04-15"],
  },
  {
    key: "janmashtami",
    eyebrow: "Season's greetings from ITO",
    name: "Happy Janmashtami",
    accent: "#2E6FB0",
    image:
      "https://static.prod-images.emergentagent.com/jobs/9cbd5b9e-f8ce-4d41-86d0-1a1eddd399f3/images/90daee6132aff2df360722518165fd085d078abaa87699197a67973ca42e314c.jpeg",
    quote:
      "Just as Krishna guided every chariot to its destiny, we steer every consignment safely home. Happy Janmashtami — may joy, devotion and prosperity fill your celebrations. Nandlala ki Jai!",
    dates: ["2026-09-04", "2027-08-24"],
  },
  {
    key: "navratri",
    eyebrow: "Season's greetings from ITO",
    name: "Happy Navratri",
    accent: "#D6336C",
    image:
      "https://static.prod-images.emergentagent.com/jobs/9cbd5b9e-f8ce-4d41-86d0-1a1eddd399f3/images/c0fe179b47feaffa58e25044dfa9ac75dae04ae7e50051b5462cc98e771acbde.jpeg",
    quote:
      "Nine nights of energy, nine forms of Shakti — and a fleet that never tires. May Maa Durga's blessings bring prosperity and safe passage to every route this Navratri.",
    dates: [
      "2026-10-11", "2026-10-12", "2026-10-13", "2026-10-14", "2026-10-15",
      "2026-10-16", "2026-10-17", "2026-10-18", "2026-10-19",
      "2027-09-29", "2027-09-30", "2027-10-01", "2027-10-02", "2027-10-03",
      "2027-10-04", "2027-10-05", "2027-10-06", "2027-10-07",
    ],
  },
  {
    key: "karva_chauth",
    eyebrow: "Season's greetings from ITO",
    name: "Happy Karva Chauth",
    accent: "#C63D5A",
    image:
      "https://static.prod-images.emergentagent.com/jobs/9cbd5b9e-f8ce-4d41-86d0-1a1eddd399f3/images/37474c2479141376947f8ffbfd108161e08aa5dc3c4c08f7ee3ddc9fcacfaa8a.jpeg",
    quote:
      "A day of devotion and the promise to return safely — the very promise our drivers make on every trip. Wishing your family love, light and togetherness this Karva Chauth.",
    dates: ["2026-10-29", "2027-10-18"],
  },
  {
    key: "dhanteras",
    eyebrow: "Season's greetings from ITO",
    name: "Happy Dhanteras",
    accent: "#E6A817",
    image:
      "https://static.prod-images.emergentagent.com/jobs/9cbd5b9e-f8ce-4d41-86d0-1a1eddd399f3/images/c0588167ec349c5868dc4c7e265d56401f2300082e9cb783e80fe38acddc69a5.jpeg",
    quote:
      "May this Dhanteras fill your ledgers with prosperity and your warehouses with plenty. When you're ready to move it all, our wheels are ready to roll. Shubh Dhanteras!",
    dates: ["2026-11-06", "2027-10-26"],
  },
  {
    key: "chhath_puja",
    eyebrow: "Season's greetings from ITO",
    name: "Happy Chhath Puja",
    accent: "#E8763A",
    image:
      "https://static.prod-images.emergentagent.com/jobs/9cbd5b9e-f8ce-4d41-86d0-1a1eddd399f3/images/d1420a0da9aee01fe7d7e9544ebc8d2af68ee13f6515f530f6b35ac96f7c72c8.jpeg",
    quote:
      "As devotees greet the rising sun across the Ganga, we greet every dawn on the highway to keep your cargo moving. May Chhathi Maiya bless your home and your business this Chhath.",
    dates: ["2026-11-15", "2026-11-16", "2026-11-17", "2027-11-05", "2027-11-06"],
  },
  {
    key: "vasant_panchami",
    eyebrow: "Season's greetings from ITO",
    name: "Happy Vasant Panchami",
    accent: "#EAB308",
    image:
      "https://static.prod-images.emergentagent.com/jobs/9cbd5b9e-f8ce-4d41-86d0-1a1eddd399f3/images/7bc1be4a8fed42f6de14f07dd97062972aad1ecb306b7ca92bd002552267ec13.jpeg",
    quote:
      "On the day we honour Saraswati, we celebrate knowledge, planning and precision — the very things that chart every route we run. Wishing you a bright and wise Vasant Panchami.",
    dates: ["2026-01-23", "2027-02-11"],
  },
  {
    key: "gudi_padwa",
    eyebrow: "Season's greetings from ITO",
    name: "Happy Gudi Padwa & Ugadi",
    accent: "#E07B2E",
    image:
      "https://static.prod-images.emergentagent.com/jobs/9cbd5b9e-f8ce-4d41-86d0-1a1eddd399f3/images/7701a2ecdbf780e01857f3a22eef707a02b5d495d891d45247ecd9e3e27ce7e6.jpeg",
    quote:
      "A new year, a new Gudi raised in hope — may your ventures rise high and your goods always arrive on time. Wishing you a prosperous new year.",
    dates: ["2026-03-19", "2027-04-08"],
    regional: {
      west: {
        name: "Happy Gudi Padwa",
        quote: "As the Gudi rises over every Maharashtrian doorway, may victory, health and prosperity fill your year. Wishing you a very happy Gudi Padwa.",
      },
      south: {
        name: "Happy Ugadi",
        quote: "A new year dawns across the Deccan — with the bittersweet taste of Ugadi Pachadi reminding us to take every turn in stride. Wishing you a prosperous Ugadi.",
      },
    },
  },
  {
    key: "mahavir_jayanti",
    eyebrow: "Season's greetings from ITO",
    name: "Happy Mahavir Jayanti",
    accent: "#B58B2C",
    image:
      "https://static.prod-images.emergentagent.com/jobs/9cbd5b9e-f8ce-4d41-86d0-1a1eddd399f3/images/d0f01f87077d90bee0fb91b48c8bd9e0dc953372ee908fc37ec90576ec209622.jpeg",
    quote:
      "Ahimsa, truth and discipline — the values that steer how we care for every consignment. On Mahavir Jayanti, may Lord Mahavir's teachings bring you peace and prosperity.",
    dates: ["2026-03-31", "2027-04-18"],
  },
  {
    key: "hanuman_jayanti",
    eyebrow: "Season's greetings from ITO",
    name: "Happy Hanuman Jayanti",
    accent: "#E4572E",
    image:
      "https://static.prod-images.emergentagent.com/jobs/9cbd5b9e-f8ce-4d41-86d0-1a1eddd399f3/images/2a697377dc89f185259ba4bb3d3a6c19e4999a30655b4311cc616c4300a8efc5.jpeg",
    quote:
      "Strength, service and boundless devotion — the qualities Bajrangbali embodies and our teams carry on every long haul. Jai Hanuman! Wishing you courage and success.",
    dates: ["2026-04-02", "2027-03-22"],
  },
  {
    key: "good_friday",
    eyebrow: "With you in reflection",
    name: "A Peaceful Good Friday",
    accent: "#7E6BA8",
    image:
      "https://static.prod-images.emergentagent.com/jobs/9cbd5b9e-f8ce-4d41-86d0-1a1eddd399f3/images/6f95fc92410607aa762fa2f083785dafdb954c32578e4aaf8af765f342a8fed0.jpeg",
    quote:
      "On this solemn day of reflection and sacrifice, we pause in gratitude and hope. Wishing you and your family a peaceful and blessed Good Friday.",
    dates: ["2026-04-03", "2027-03-26"],
  },
  {
    key: "baisakhi",
    eyebrow: "Season's greetings from ITO",
    name: "Happy Baisakhi",
    accent: "#E0A21B",
    image:
      "https://static.prod-images.emergentagent.com/jobs/9cbd5b9e-f8ce-4d41-86d0-1a1eddd399f3/images/7195b004c4f5a5e31039b2ac1d2aa2f4717a008a80f3e5e9c74ab81a7e686d39.jpeg",
    quote:
      "As the harvest is gathered and a new year begins, may your fields, your ledgers and your warehouses overflow. Wishing you a joyous, prosperous new year.",
    dates: ["2026-04-14", "2026-04-15", "2027-04-14", "2027-04-15"],
    regional: {
      north: {
        name: "Happy Baisakhi",
        quote: "To the beat of the dhol and the gleam of golden wheat, may this Baisakhi fill your home with joy and your business with plenty. Jo bole so nihaal!",
      },
      south: {
        name: "Happy Vishu & Puthandu",
        quote: "A new year, a fresh Vishukkani and new beginnings across the South. May the year ahead bring prosperity, good fortune and smooth roads. Happy Vishu and Puthandu!",
      },
      east: {
        name: "Shubho Nabobarsho",
        quote: "A new Bengali year begins with sweetness and hope. May your ventures flourish and every consignment reach home on time. Shubho Nabobarsho!",
      },
      northeast: {
        name: "Happy Rongali Bihu",
        quote: "Bihu geet, new harvest and new hope across the North-East. May this Rongali Bihu bring joy to your home and prosperity to your work. Happy Bihu!",
      },
    },
  },
  {
    key: "buddha_purnima",
    eyebrow: "Season's greetings from ITO",
    name: "Happy Buddha Purnima",
    accent: "#D9A441",
    image:
      "https://static.prod-images.emergentagent.com/jobs/9cbd5b9e-f8ce-4d41-86d0-1a1eddd399f3/images/cb1e70384af4ae69d026435e9d43017ee455eff696ceb5af02312b973e3da950.jpeg",
    quote:
      "On Buddha Purnima, we remember the path of peace, patience and mindfulness — the calm that steadies every journey. May light and compassion guide your way.",
    dates: ["2026-05-01", "2027-05-20"],
  },
  {
    key: "dahi_handi",
    eyebrow: "Season's greetings from ITO",
    name: "Happy Dahi Handi",
    accent: "#E86A2B",
    image:
      "https://static.prod-images.emergentagent.com/jobs/9cbd5b9e-f8ce-4d41-86d0-1a1eddd399f3/images/01004197a609eae45094b2da262eeac443eb8e0d5022bd8ea08b156be3eee748.jpeg",
    quote:
      "Reach higher, together — that's the spirit of the human pyramid, and of every team that moves your cargo across India. Govinda aala re! Happy Dahi Handi.",
    dates: ["2026-09-05", "2027-08-25"],
  },
  {
    key: "govardhan_puja",
    eyebrow: "Season's greetings from ITO",
    name: "Happy Govardhan Puja",
    accent: "#D98324",
    image:
      "https://static.prod-images.emergentagent.com/jobs/9cbd5b9e-f8ce-4d41-86d0-1a1eddd399f3/images/f3169250c6769ad816bd8983c35cdd0c82794fbe244ef0aa7dcf7348d1b95cea.jpeg",
    quote:
      "Just as Krishna lifted Govardhan to shelter his people, we lift every load to shelter your business. Wishing you a blessed Govardhan Puja and a bountiful Annakut.",
    dates: ["2026-11-10", "2027-10-31"],
  },
  {
    key: "bhai_dooj",
    eyebrow: "Season's greetings from ITO",
    name: "Happy Bhai Dooj",
    accent: "#D1495B",
    image:
      "https://static.prod-images.emergentagent.com/jobs/9cbd5b9e-f8ce-4d41-86d0-1a1eddd399f3/images/f5a787abc7d0c20718b1d0eca27afe0914c5f50fc1ed4467a51fc7639b454df4.jpeg",
    quote:
      "A bond of protection and love between siblings — the very promise we make to every parcel in our care. Happy Bhai Dooj to you and your family.",
    dates: ["2026-11-11", "2027-11-01"],
  },
];

// Today's date in India (IST) as "YYYY-MM-DD".
function istTodayParts() {
  const s = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
  const [y, m, d] = s.split("-").map(Number);
  return { iso: s, y, m, d };
}

// Returns the festival active today, or the one matching `overrideKey` (for preview),
// or null. Preview: append ?festival=diwali (or ?fest=diwali) to the URL.
export function getActiveFestival(overrideKey) {
  if (overrideKey) return FESTIVALS.find((f) => f.key === overrideKey) || null;
  const { iso, m, d } = istTodayParts();
  for (const f of FESTIVALS) {
    if (f.dates && f.dates.includes(iso)) return f;
    if (f.annual && f.annual.month === m && f.annual.day === d) return f;
    if (
      f.annualRange &&
      f.annualRange.month === m &&
      d >= f.annualRange.from &&
      d <= f.annualRange.to
    )
      return f;
  }
  return null;
}

// Short label used in the countdown teaser ("Diwali greetings live in 3 days").
const SHORT = {
  new_year: "New Year",
  makar_sankranti: "Sankranti & Pongal",
  republic_day: "Republic Day",
  holi: "Holi",
  eid_fitr: "Eid",
  eid_adha: "Bakrid",
  independence_day: "Independence Day",
  onam: "Onam",
  raksha_bandhan: "Raksha Bandhan",
  ganesh_chaturthi: "Ganesh Chaturthi",
  dussehra: "Dussehra",
  gandhi_jayanti: "Gandhi Jayanti",
  diwali: "Diwali",
  guru_nanak: "Gurpurab",
  christmas: "Christmas",
  maha_shivratri: "Maha Shivratri",
  ram_navami: "Ram Navami",
  janmashtami: "Janmashtami",
  navratri: "Navratri",
  karva_chauth: "Karva Chauth",
  dhanteras: "Dhanteras",
  chhath_puja: "Chhath Puja",
  vasant_panchami: "Vasant Panchami",
  gudi_padwa: "Gudi Padwa & Ugadi",
  mahavir_jayanti: "Mahavir Jayanti",
  hanuman_jayanti: "Hanuman Jayanti",
  good_friday: "Good Friday",
  baisakhi: "Baisakhi",
  buddha_purnima: "Buddha Purnima",
  dahi_handi: "Dahi Handi",
  govardhan_puja: "Govardhan Puja",
  bhai_dooj: "Bhai Dooj",
};
export function festivalShort(f) {
  return SHORT[f.key] || f.name;
}

const pad = (n) => String(n).padStart(2, "0");
const utcDay = (iso) => new Date(iso + "T00:00:00Z");

// Next Gregorian occurrence (Date at UTC midnight) of a festival, strictly after `today`.
function nextOccurrence(f, today, y) {
  const cands = [];
  if (f.dates) f.dates.forEach((ds) => cands.push(utcDay(ds)));
  if (f.annual) {
    cands.push(utcDay(`${y}-${pad(f.annual.month)}-${pad(f.annual.day)}`));
    cands.push(utcDay(`${y + 1}-${pad(f.annual.month)}-${pad(f.annual.day)}`));
  }
  if (f.annualRange) {
    cands.push(utcDay(`${y}-${pad(f.annualRange.month)}-${pad(f.annualRange.from)}`));
    cands.push(utcDay(`${y + 1}-${pad(f.annualRange.month)}-${pad(f.annualRange.from)}`));
  }
  const future = cands.filter((c) => c.getTime() > today.getTime()).sort((a, b) => a - b);
  return future[0] || null;
}

// Nearest upcoming festival within `withinDays` (exclusive of today). Returns { festival, days } or null.
// Preview: append ?teaser=diwali&days=3 to force it.
export function getUpcomingFestival(withinDays = 7, override) {
  if (override && override.key) {
    const f = FESTIVALS.find((x) => x.key === override.key);
    if (f) return { festival: f, days: override.days || 3 };
    return null;
  }
  const { iso, y } = istTodayParts();
  const today = utcDay(iso);
  let best = null;
  for (const f of FESTIVALS) {
    const occ = nextOccurrence(f, today, y);
    if (!occ) continue;
    const days = Math.round((occ.getTime() - today.getTime()) / 86400000);
    if (days >= 1 && days <= withinDays && (!best || days < best.days)) {
      best = { festival: f, days };
    }
  }
  return best;
}

// ---- Regional greeting variants (auto-localised by the visitor's Indian state) ----
// Maps each state/UT to a broad cultural zone used for festival name variants.
const STATE_ZONE = {
  // South
  "tamil nadu": "south", "kerala": "south", "karnataka": "south",
  "andhra pradesh": "south", "telangana": "south", "puducherry": "south", "pondicherry": "south",
  // North East
  "assam": "northeast", "arunachal pradesh": "northeast", "nagaland": "northeast",
  "manipur": "northeast", "mizoram": "northeast", "tripura": "northeast",
  "meghalaya": "northeast", "sikkim": "northeast",
  // North
  "punjab": "north", "haryana": "north", "himachal pradesh": "north", "delhi": "north",
  "national capital territory of delhi": "north", "uttarakhand": "north", "uttar pradesh": "north",
  "rajasthan": "north", "jammu and kashmir": "north", "ladakh": "north", "chandigarh": "north",
  // West
  "maharashtra": "west", "gujarat": "west", "goa": "west",
  "dadra and nagar haveli": "west", "daman and diu": "west",
  // East
  "west bengal": "east", "bihar": "east", "odisha": "east", "orissa": "east",
  "jharkhand": "east", "chhattisgarh": "east", "madhya pradesh": "east",
};

export function stateToZone(state) {
  if (!state) return null;
  return STATE_ZONE[String(state).trim().toLowerCase()] || null;
}

// Merge a festival's regional override (name/quote/eyebrow/accent/image) for a zone.
export function applyRegion(festival, zone) {
  if (!festival || !zone || !festival.regional || !festival.regional[zone]) return festival;
  return { ...festival, ...festival.regional[zone] };
}

// Best-effort IP → Indian-state → zone lookup (keyless, CORS-friendly). Fails silently.
export async function detectRegionZone() {
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 5000);
    const r = await fetch("https://ipwho.is/", { signal: ctrl.signal });
    clearTimeout(t);
    const j = await r.json();
    const inIndia = j && (j.country_code === "IN" || j.country === "India");
    if (inIndia && j.region) return stateToZone(j.region);
  } catch (e) {
    /* ignore — fall back to the default national greeting */
  }
  return null;
}

